import React, { createContext, useContext, useMemo, useState, type ReactElement } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Outlet } from 'react-router-dom';

import { defaultValues } from '@/helpers/formSolicitudes';
import { resolver } from '@/helpers/validationsSolicitudes';
import type { DataSolicitud, ISolicitudContext } from '@/interfaces/context/ISolicitud.context.interface';
import type { FormValues } from '@/interfaces/model/don.interface';

import { useGlobalContext } from './global.context';

const contextDefaultValue: ISolicitudContext = {
	activeStep: 0,
	setActiveStep: () => undefined,
	data: undefined,
	setData: () => undefined,
	maxStep: 0,
	setMaxStep: () => undefined,
	requerirDocumentacion: true,
	setRequerirDocumentacion: () => undefined,
	getFormValues: () => defaultValues() as any,
};

const SolicitudContext = createContext<ISolicitudContext>(contextDefaultValue);

const SolicitudProvider: React.FC = (): ReactElement => {
	const [activeStep, setActiveStep] = useState(0);
	const [maxStep, setMaxStep] = useState(0);
	const [requerirDocumentacion, setRequerirDocumentacion] = useState(true);
	const [data, setData] = useState<DataSolicitud>();
	const {
		maestrosTramite: { tipoDocumento, tipoIntervencion, configuracionTramite },
	} = useGlobalContext();
	const methods = useForm<FormValues>({
		defaultValues: defaultValues(),
		mode: 'onChange',
		resolver: resolver({
			tipoIntervencionMaestro: tipoIntervencion,
			tipoDocumentoMaestro: tipoDocumento,
			configuracionTramite,
		}),
	});

	const value: ISolicitudContext = useMemo(
		() => ({
			activeStep,
			data,
			setData,
			setActiveStep,
			maxStep,
			setMaxStep,
			getFormValues: methods.watch,
			requerirDocumentacion,
			setRequerirDocumentacion,
		}),
		[
			activeStep,
			setActiveStep,
			data,
			setData,
			maxStep,
			setMaxStep,
			requerirDocumentacion,
			setRequerirDocumentacion,
		],
	);

	return (
		<SolicitudContext.Provider value={value}>
			<FormProvider {...methods}>
				<Outlet />
			</FormProvider>
		</SolicitudContext.Provider>
	);
};

const useSolicitudContext = (): ISolicitudContext => {
	const context = useContext(SolicitudContext);

	if (context === null) {
		throw new Error('useSolicitud must be used within provider');
	}

	return context;
};

export { SolicitudContext, SolicitudProvider, useSolicitudContext };
